'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import { useCuratorSnackbar } from '../../common/hooks';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';

interface Props {
  className?: string;
}

export const ShopLinkCopyButton = ({ className }: Props) => {
  const snackbar = useCuratorSnackbar();
  const { service } = useDeviceInfo();

  const { data, isPending } = useQuery({
    ...curatorQueries.link.getLinkCuration({}),
  });

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });

  const onLinkCopyClick = async () => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_shop_manage',
          section_name: 'CurationShopInfo',
          section_title: 'CurationShopInfo',
          button_id: 'share_link',
          button_name: '샵링크 복사',
          position: -1,
        },
      });
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curatorlink_copy',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: userInfo?.snsTitle ?? '',
        name: 'shop',
        section_name: 'curator_profile',
      });
    }

    try {
      // FIXME: shortLink를 동적으로 Fetch하면 Safari Clipboard API 정책에 위배되어 미리 fetch해둔 값으로 갈음한다.
      // 참고 url: https://www.reddit.com/r/webdev/comments/1ge5ou7/why_does_clipboard_access_fail_in_safari_after_an/
      if (!data?.shortLink) {
        snackbar.error('단축링크를 가져올 수 없습니다.');
        return;
      }

      await navigator.clipboard.writeText(data.shortLink);
      snackbar.success('큐레이션 샵 링크가 복사되었습니다.');
    } catch (_error) {
      snackbar.error('링크 복사 중 오류가 발생했습니다.');
    }
  };

  return (
    <Button
      size="32"
      className={twMerge('flex items-center gap-[2px]', className)}
      onClick={onLinkCopyClick}
      isLoading={isPending}
    >
      <svg
        width="17"
        height="16"
        viewBox="0 0 17 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.9502 4.80002V13.3C3.9502 13.4657 4.08451 13.6 4.2502 13.6L11.1502 13.6M13.2502 11.2H6.6502C6.48451 11.2 6.3502 11.0657 6.3502 10.9V2.70002C6.3502 2.53434 6.48451 2.40002 6.6502 2.40002H13.2502C13.4159 2.40002 13.5502 2.53434 13.5502 2.70003V10.9C13.5502 11.0657 13.4159 11.2 13.2502 11.2Z"
          stroke="white"
        />
      </svg>

      <Typography variant="body13pxMed">샵 링크 복사</Typography>
    </Button>
  );
};
